import { useState } from "react";
import { toast } from "sonner";
import { BellPlus, BellRing, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { usePoll } from "@/hooks/usePoll";
import { fmtPrice, fmtDateTime } from "@/lib/format";
import { Empty } from "@/components/kit";

export const AlertForm = ({ q, onCreated }) => {
  const [target, setTarget] = useState("");
  const [note, setNote] = useState("");
  const t = Number(target);
  const cond = t ? (t > q.price ? "above" : "below") : null;
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/me/alerts", { symbol: q.symbol, target: t, note: note || null });
      toast.success(`Alerta criado: ${q.name} ${cond === "above" ? "acima de" : "abaixo de"} ${fmtPrice(t, q.decimals)}`);
      setTarget(""); setNote(""); onCreated?.();
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="price-alert-form">
      <div className="text-sm text-muted-foreground">Cotação atual: <span className="num text-foreground">{fmtPrice(q.price, q.decimals)}</span></div>
      <div className="space-y-1.5"><Label>Avisar quando o preço atingir</Label>
        <Input type="number" step="any" required className="h-11 num" value={target} onChange={(e) => setTarget(e.target.value)} placeholder={fmtPrice(q.price, q.decimals)} data-testid="alert-target-input" /></div>
      {cond && <p className={`text-xs ${cond === "above" ? "up" : "down"}`} data-testid="alert-condition-hint">Você será avisado quando o preço {cond === "above" ? "SUBIR até" : "CAIR até"} {fmtPrice(t, q.decimals)}.</p>}
      <Input placeholder="Lembrete (opcional)" className="h-11" maxLength={120} value={note} onChange={(e) => setNote(e.target.value)} data-testid="alert-note-input" />
      <Button className="h-11 w-full" data-testid="alert-submit-button"><BellPlus className="mr-2 h-4 w-4" />Criar alerta</Button>
    </form>
  );
};

export const AlertsList = ({ symbol, refreshKey = 0 }) => {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/me/alerts", { params: { symbol } }).then((r) => setRows(r.data));
  usePoll(() => load().catch(() => {}), 10000, [symbol, refreshKey]);
  const cancel = async (id) => { try { await api.delete(`/me/alerts/${id}`); toast.success("Alerta removido"); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (!rows.length) return <Empty text="Nenhum alerta de preço." testid="alerts-empty" />;
  return (
    <div className="divide-y divide-border" data-testid="alerts-list">
      {rows.map((a) => (
        <div key={a.id} className="flex items-center gap-3 py-3 text-sm" data-testid={`alert-row-${a.id}`}>
          <BellRing className={`h-4 w-4 ${a.status === "triggered" ? "text-primary" : "text-muted-foreground"}`} />
          <div className="flex-1">
            <div className="font-medium">{!symbol && `${a.name} · `}{a.condition === "above" ? "Acima de" : "Abaixo de"} <span className="num">{fmtPrice(a.target, a.decimals)}</span></div>
            <div className="text-xs text-muted-foreground">
              {a.status === "triggered" ? `Disparado em ${fmtDateTime(a.triggered_at)} a ${fmtPrice(a.triggered_price, a.decimals)}` : `Ativo desde ${fmtDateTime(a.created_at)}`}{a.note && ` · ${a.note}`}
            </div>
          </div>
          <span className={`text-[10px] font-bold ${a.status === "triggered" ? "text-primary" : "up"}`} data-testid={`alert-status-${a.id}`}>{a.status === "triggered" ? "DISPARADO" : "ATIVO"}</span>
          <Button size="icon" variant="ghost" aria-label="Remover alerta" onClick={() => cancel(a.id)} data-testid={`alert-cancel-${a.id}`}><X className="h-4 w-4" /></Button>
        </div>
      ))}
    </div>
  );
};

export const AlertButton = ({ q }) => {
  const [open, setOpen] = useState(false);
  const [k, setK] = useState(0);
  return (
    <>
      <Button size="icon" variant="ghost" aria-label="Criar alerta de preço" onClick={() => setOpen(true)} data-testid={`alert-open-${q.symbol}`}><BellPlus className="h-4 w-4" /></Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="price-alert-dialog">
          <DialogHeader><DialogTitle>Alerta de preço — {q.name}</DialogTitle>
            <DialogDescription>Você receberá uma notificação quando a cotação atingir o valor definido.</DialogDescription></DialogHeader>
          <AlertForm q={q} onCreated={() => setK(k + 1)} />
          <AlertsList symbol={q.symbol} refreshKey={k} />
        </DialogContent>
      </Dialog>
    </>
  );
};

export const AlertsCard = ({ q }) => {
  const [k, setK] = useState(0);
  return (
    <div className="surface p-5 md:p-6 mt-5" data-testid="price-alerts-card">
      <div className="eyebrow mb-4 flex items-center gap-1.5"><BellRing className="h-3.5 w-3.5 text-primary" />Alertas de preço</div>
      <AlertForm q={q} onCreated={() => setK(k + 1)} />
      <div className="mt-3"><AlertsList symbol={q.symbol} refreshKey={k} /></div>
    </div>
  );
};
